import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import api, { fileUrl } from "../lib/api";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Plus, Trash2, Upload, X, Package, MessageSquare, Truck, AlertTriangle, Pencil, Pause, Play } from "lucide-react";
import { InquiryConversation } from "../components/InquiryConversation";
import { PostedJobsManager } from "../components/PostedJobsManager";


export default function SellerDashboard() {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [categories, setCategories] = useState([]);
  const approved = user.status === "approved";
  const totalUnread = inquiries.reduce((s, i) => s + (i.unread || 0), 0);

  const reload = useCallback(async () => {
    const [p, i, j] = await Promise.all([
      api.get("/seller/products"), api.get("/seller/inquiries"), api.get("/seller/transport-jobs"),
    ]);
    setProducts(p.data); setInquiries(i.data); setJobs(j.data);
  }, []);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
    reload();
  }, [reload]);

  const updateInquiry = async (inquiry_id, status) => {
    await api.put(`/inquiries/${inquiry_id}/status?status=${status}`);
    toast.success("Inquiry updated");
    reload();
  };

  const deleteProduct = async (pid) => {
    await api.delete(`/products/${pid}`);
    toast.success("Listing removed");
    reload();
  };

  const setProductStatus = async (pid, status) => {
    await api.put(`/products/${pid}/status?status=${status}`);
    toast.success(status === "paused" ? "Listing paused" : "Listing reactivated");
    reload();
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="border-b border-foreground bg-foreground text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <span className="label-caps text-accent">Seller console</span>
            <h1 className="font-display font-black text-3xl tracking-tight mt-1">{user.company_name || user.name}</h1>
          </div>
          <Badge className={`rounded-none uppercase tracking-wider ${approved ? "bg-accent text-foreground" : "bg-white/20 text-white"}`} data-testid="seller-status">
            {user.status}
          </Badge>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
        {!approved && (
          <div className="border border-accent bg-accent/10 p-4 flex items-start gap-3 mb-6" data-testid="pending-banner">
            <AlertTriangle className="h-5 w-5 text-foreground shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">Your seller account is pending approval</p>
              <p className="text-sm text-muted-foreground">You can explore the dashboard, but listing products and posting delivery jobs unlock once an admin approves you.</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-border border border-border mb-8">
          {[["Listings", products.length, Package], ["Inquiries", inquiries.length, MessageSquare], ["Delivery jobs", jobs.length, Truck]].map(([l, v, Icon], i) => (
            <div key={i} className="bg-white p-5">
              <Icon className="h-5 w-5 text-primary mb-2" />
              <div className="font-display font-black text-3xl">{v}</div>
              <div className="label-caps text-muted-foreground">{l}</div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="listings">
          <TabsList className="rounded-none bg-muted border border-border">
            <TabsTrigger value="listings" className="rounded-none" data-testid="tab-listings">Listings</TabsTrigger>
            <TabsTrigger value="inquiries" className="rounded-none" data-testid="tab-inquiries">
              Inquiries
              {totalUnread > 0 && (
                <span className="ml-2 inline-flex items-center justify-center h-4 min-w-4 px-1 bg-destructive text-white text-[10px] font-bold rounded-full" data-testid="inquiries-unread-badge">{totalUnread}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="jobs" className="rounded-none" data-testid="tab-jobs">Delivery Jobs</TabsTrigger>
          </TabsList>

          {/* Listings */}
          <TabsContent value="listings" className="mt-6">
            <div className="flex justify-end mb-4">
              <ProductDialog categories={categories} disabled={!approved} onSaved={reload} />
            </div>
            {products.length === 0 ? (
              <Empty text="No listings yet. Add your first product." />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {products.map((p) => {
                  const paused = p.status === "paused";
                  return (
                  <div key={p.product_id} className="border border-border" data-testid={`seller-product-${p.product_id}`}>
                    <div className="aspect-[4/3] bg-muted border-b border-border overflow-hidden relative">
                      <img src={p.images?.[0] ? fileUrl(p.images[0]) : "https://images.pexels.com/photos/3806252/pexels-photo-3806252.jpeg"}
                           alt={p.title} className={`h-full w-full object-cover ${paused ? "opacity-50" : ""}`} />
                      <Badge className={`absolute top-2 left-2 rounded-none text-[10px] uppercase tracking-wider ${paused ? "bg-muted text-foreground" : "bg-accent text-foreground"}`} data-testid={`product-status-${p.product_id}`}>
                        {paused ? "Paused" : "Active"}
                      </Badge>
                    </div>
                    <div className="p-4">
                      <h3 className="font-display font-bold text-lg tracking-tight line-clamp-1">{p.title}</h3>
                      <span className="font-display font-black block mt-1">£{Number(p.price).toLocaleString()}</span>
                      <div className="flex items-center gap-2 mt-3 flex-wrap">
                        <ProductDialog categories={categories} product={p} onSaved={reload}
                          trigger={<Button size="sm" variant="outline" className="rounded-none" data-testid={`edit-product-${p.product_id}`}><Pencil className="h-4 w-4 mr-1" /> Edit</Button>} />
                        {paused ? (
                          <Button size="sm" variant="outline" className="rounded-none border-primary text-primary hover:bg-primary hover:text-white"
                                  onClick={() => setProductStatus(p.product_id, "active")} data-testid={`reactivate-product-${p.product_id}`}><Play className="h-4 w-4 mr-1" /> Reactivate</Button>
                        ) : (
                          <Button size="sm" variant="outline" className="rounded-none"
                                  onClick={() => setProductStatus(p.product_id, "paused")} data-testid={`pause-product-${p.product_id}`}><Pause className="h-4 w-4 mr-1" /> Pause</Button>
                        )}
                        <Button size="sm" variant="outline" className="rounded-none border-destructive text-destructive hover:bg-destructive hover:text-white"
                                onClick={() => deleteProduct(p.product_id)} data-testid={`delete-product-${p.product_id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Inquiries */}
          <TabsContent value="inquiries" className="mt-6">
            {inquiries.length === 0 ? <Empty text="No inquiries yet." /> : (
              <div className="border border-border divide-y divide-border">
                {inquiries.map((inq) => (
                  <div key={inq.inquiry_id} className="p-4" data-testid={`inquiry-${inq.inquiry_id}`}>
                    <InquiryConversation
                      inquiry={inq}
                      viewerRole="seller"
                      onChange={reload}
                      subtitle={`From ${inq.buyer_name} · ${inq.buyer_email}${inq.contact_phone ? ` · ${inq.contact_phone}` : ""}`}
                      extraControls={
                        <Select value={inq.status} onValueChange={(v) => updateInquiry(inq.inquiry_id, v)}>
                          <SelectTrigger className="rounded-none w-36 h-9" data-testid={`inquiry-status-${inq.inquiry_id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">New</SelectItem>
                            <SelectItem value="responded">Responded</SelectItem>
                            <SelectItem value="closed">Closed</SelectItem>
                          </SelectContent>
                        </Select>
                      }
                    />
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Jobs */}
          <TabsContent value="jobs" className="mt-6">
            <PostedJobsManager disabled={!approved} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function Empty({ text }) {
  return <div className="border border-dashed border-border p-16 text-center text-muted-foreground" data-testid="empty-state">{text}</div>;
}

function ImagePicker({ images, setImages }) {
  const [uploading, setUploading] = useState(false);
  const onPick = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setImages((prev) => [...prev, data.path]);
      }
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-2">
        {images.map((img, i) => (
          <div key={i} className="relative h-16 w-16 border border-border">
            <img src={fileUrl(img)} alt="" className="h-full w-full object-cover" />
            <button type="button" onClick={() => setImages(images.filter((_, x) => x !== i))}
                    className="absolute -top-2 -right-2 bg-destructive text-white h-5 w-5 flex items-center justify-center" data-testid={`remove-image-${i}`}>
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
        <label className="h-16 w-16 border border-dashed border-foreground flex items-center justify-center cursor-pointer hover:bg-muted" data-testid="image-upload-label">
          {uploading ? <span className="text-xs">…</span> : <Upload className="h-5 w-5 text-muted-foreground" />}
          <input type="file" accept="image/*" multiple className="hidden" onChange={onPick} data-testid="image-upload-input" />
        </label>
      </div>
    </div>
  );
}

function ProductDialog({ categories, disabled, onSaved, product, trigger }) {
  const isEdit = !!product;
  const empty = { title: "", description: "", category: "New tyres", price: "", brand: "", size: "", condition: "new", tread_depth: "", quantity: 1, location: "" };
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(empty);
  const [images, setImages] = useState([]);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    if (open && isEdit) {
      setForm({
        title: product.title || "", description: product.description || "", category: product.category || "New tyres",
        price: product.price ?? "", brand: product.brand || "", size: product.size || "", condition: product.condition || "new",
        tread_depth: product.tread_depth || "", quantity: product.quantity ?? 1, location: product.location || "",
      });
      setImages(product.images || []);
    }
  }, [open, isEdit, product]);

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form, price: parseFloat(form.price), quantity: parseInt(form.quantity), images };
      if (isEdit) {
        await api.put(`/products/${product.product_id}`, body);
        toast.success("Listing updated");
      } else {
        await api.post("/products", body);
        toast.success("Listing published");
        setForm(empty);
        setImages([]);
      }
      setOpen(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button disabled={disabled} className="rounded-none bg-primary hover:bg-primary/90" data-testid="add-product-btn">
            <Plus className="h-4 w-4 mr-1" /> Add product
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="rounded-none max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">{isEdit ? "Edit listing" : "New listing"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="label-caps">Photos</Label><div className="mt-2"><ImagePicker images={images} setImages={setImages} /></div></div>
          <div><Label className="label-caps">Title</Label><Input value={form.title} onChange={upd("title")} className="rounded-none mt-1 h-10" data-testid="product-title-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="label-caps">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="rounded-none mt-1 h-10" data-testid="product-category-input"><SelectValue /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c.category_id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="label-caps">Condition</Label>
              <Select value={form.condition} onValueChange={(v) => setForm({ ...form, condition: v })}>
                <SelectTrigger className="rounded-none mt-1 h-10" data-testid="product-condition-input"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">New</SelectItem><SelectItem value="used">Used</SelectItem><SelectItem value="refurbished">Refurbished</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="label-caps">Brand</Label><Input value={form.brand} onChange={upd("brand")} className="rounded-none mt-1 h-10" data-testid="product-brand-input" /></div>
            <div><Label className="label-caps">Size</Label><Input value={form.size} onChange={upd("size")} className="rounded-none mt-1 h-10" placeholder="e.g. 295/80 R22.5" data-testid="product-size-input" /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="label-caps">Tread depth</Label><Input value={form.tread_depth} onChange={upd("tread_depth")} className="rounded-none mt-1 h-10" placeholder="e.g. 8mm" data-testid="product-tread-input" /></div>
            <div><Label className="label-caps">Price £</Label><Input type="number" value={form.price} onChange={upd("price")} className="rounded-none mt-1 h-10" data-testid="product-price-input" /></div>
            <div><Label className="label-caps">Qty</Label><Input type="number" value={form.quantity} onChange={upd("quantity")} className="rounded-none mt-1 h-10" data-testid="product-qty-input" /></div>
          </div>
          <div><Label className="label-caps">Location</Label><Input value={form.location} onChange={upd("location")} className="rounded-none mt-1 h-10" data-testid="product-location-input" /></div>
          <div><Label className="label-caps">Description</Label><Textarea value={form.description} onChange={upd("description")} rows={3} className="rounded-none mt-1" data-testid="product-description-input" /></div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving || !form.title || !form.price} className="rounded-none bg-primary hover:bg-primary/90" data-testid="save-product-btn">
            {saving ? (isEdit ? "Saving…" : "Publishing…") : (isEdit ? "Save changes" : "Publish listing")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
